import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { mealPlanService } from './meal-plan.service'
import { showToast } from '@/shared/utils/toast'
import { useRecipes } from '@/features/recipes/useRecipes'
import type { WeeklyMealPlan, AssignMealSlotPayload } from './types'

export function useAssignMealSlot(weekStartDate: string) {
  const queryClient = useQueryClient()
  const { t } = useTranslation('meal-plan')
  const { data: recipesData } = useRecipes()

  return useMutation({
    mutationFn: (payload: AssignMealSlotPayload) => mealPlanService.assignMealSlot(payload),
    onMutate: async (payload) => {
      const queryKey = ['meal-plan', weekStartDate]
      await queryClient.cancelQueries({ queryKey })
      const prev = queryClient.getQueryData<WeeklyMealPlan>(queryKey)

      // Look up recipe details from the cache for the optimistic update
      const recipe = recipesData?.find((r) => r.id === payload.recipeId)

      queryClient.setQueryData<WeeklyMealPlan>(queryKey, (old) => {
        if (!old) return old
        return {
          ...old,
          slots: old.slots.map((slot) =>
            slot.dayOfWeek === payload.dayOfWeek
              ? {
                  ...slot,
                  recipeId: payload.recipeId,
                  recipeName: recipe?.name ?? null,
                  servingSize: recipe?.servingSize ?? null,
                  servingMultiplier: 1.0,
                }
              : slot,
          ),
        }
      })

      return { prev }
    },
    onError: (_err, _payload, ctx) => {
      queryClient.setQueryData(['meal-plan', weekStartDate], ctx?.prev)
      showToast(t('error.assignFailed'))
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['meal-plan', weekStartDate] })
    },
  })
}
